/**
 * Dialog para editar un parámetro del sistema — Fase 8
 *
 * Solo edita `valor` (y opcionalmente `descripcion`). La `clave` y el
 * `tipo` no se modifican desde la UI — los crea el desarrollo via
 * migration.
 *
 * Validación: según el `tipo` del param. Number con tope MONTO_MAX para
 * evitar overflow accidental; coef de mora con rango 0-1 (porcentaje
 * diario razonable).
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  CircularProgress,
  Typography,
  FormControlLabel,
  Switch,
  Chip,
  Box,
} from '@mui/material';
import { useUpdateParametro } from '../../hooks/useParametros';
import {
  type Parametro,
  parseValor,
  getTipoLabel,
  getTipoColor,
} from '../../types/parametro';

interface Props {
  open: boolean;
  parametro: Parametro | null;
  onClose: () => void;
}

export default function ParametroDialog({ open, parametro, onClose }: Props) {
  const update = useUpdateParametro();

  const [valor, setValor] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [valorError, setValorError] = useState<string | null>(null);

  useEffect(() => {
    if (open && parametro) {
      setValor(parametro.valor);
      setDescripcion(parametro.descripcion ?? '');
      setValorError(null);
    }
  }, [open, parametro]);

  if (!parametro) return null;

  const handleSubmit = async () => {
    // Validación tipada
    const valorAValidar =
      parametro.tipo === 'boolean'
        ? valor // ya viene 'true' o 'false'
        : valor.trim();

    const parsed = parseValor(valorAValidar, parametro.tipo);
    if (!parsed.ok) {
      setValorError(parsed.error);
      return;
    }

    // Validación de rango específica por clave conocida
    if (parametro.clave === 'mora_coef_diario') {
      const n = parsed.value as number;
      if (n < 0 || n > 1) {
        setValorError(
          'El coeficiente de mora diario debe estar entre 0 y 1 (ej: 0.001 = 0,1% por día)',
        );
        return;
      }
    }
    if (parametro.clave === 'dias_vencimiento_cupon') {
      const n = parsed.value as number;
      if (n < 1 || n > 365 || !Number.isInteger(n)) {
        setValorError('Debe ser un entero entre 1 y 365 días');
        return;
      }
    }
    if (parametro.clave === 'pp_match_dias_dup') {
      const n = parsed.value as number;
      if (n < 0 || n > 30 || !Number.isInteger(n)) {
        setValorError('Debe ser un entero entre 0 y 30 días');
        return;
      }
    }

    try {
      await update.mutateAsync({
        clave: parametro.clave,
        valor: valorAValidar,
        descripcion: descripcion.trim() || null,
      });
      onClose();
    } catch {
      // se muestra abajo
    }
  };

  const apiError = update.error as Error | null;

  // Render del input según el tipo
  const renderInput = () => {
    if (parametro.tipo === 'boolean') {
      const checked = valor === 'true' || valor === '1';
      return (
        <FormControlLabel
          control={
            <Switch
              checked={checked}
              onChange={(e) => setValor(e.target.checked ? 'true' : 'false')}
            />
          }
          label={checked ? 'Activado (true)' : 'Desactivado (false)'}
        />
      );
    }
    if (parametro.tipo === 'json') {
      return (
        <TextField
          fullWidth
          multiline
          rows={6}
          required
          label="Valor (JSON)"
          value={valor}
          onChange={(e) => {
            setValor(e.target.value);
            setValorError(null);
          }}
          error={!!valorError}
          helperText={valorError ?? 'JSON válido. Se valida antes de guardar.'}
          InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
        />
      );
    }
    return (
      <TextField
        fullWidth
        required
        label="Valor"
        type={parametro.tipo === 'number' ? 'number' : 'text'}
        value={valor}
        onChange={(e) => {
          setValor(e.target.value);
          setValorError(null);
        }}
        error={!!valorError}
        helperText={
          valorError ??
          (parametro.tipo === 'number' ? 'Valor numérico (acepta decimales)' : '')
        }
        inputProps={
          parametro.tipo === 'number' ? { step: 'any' } : { maxLength: 1000 }
        }
      />
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Editar parámetro</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {apiError && (
            <Alert severity="error">{apiError.message}</Alert>
          )}

          <Box>
            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
              <Typography variant="body2" color="text.secondary">
                Clave
              </Typography>
              <Chip
                label={getTipoLabel(parametro.tipo)}
                size="small"
                color={getTipoColor(parametro.tipo)}
                variant="outlined"
              />
            </Stack>
            <Typography variant="body1" fontFamily="monospace" fontWeight={600}>
              {parametro.clave}
            </Typography>
          </Box>

          <TextField
            label="Descripción"
            fullWidth
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            multiline
            rows={2}
            inputProps={{ maxLength: 500 }}
          />

          {renderInput()}

          <Alert severity="info" sx={{ fontSize: 13 }}>
            Este cambio afecta el comportamiento del sistema en{' '}
            <strong>tiempo real</strong>. Los cálculos posteriores usarán el
            nuevo valor (los cupones ya pagados conservan su histórico).
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={update.isPending}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={update.isPending}
          startIcon={
            update.isPending ? <CircularProgress size={18} color="inherit" /> : null
          }
        >
          {update.isPending ? 'Guardando...' : 'Guardar cambios'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
